import { CategoryAxis, ColumnSeries, ValueAxis, XYChart } from "@amcharts/amcharts4/charts";
import { color, create, NumberFormatter } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import WebFont from "webfontloader";
import "../../../assets/styles/component/legend.scss";
import { BLACK, CCS_ANALYSIS_COLORS, WHITE } from "../../../constants";
import LoadingEllipsis from "../../Loading/LoadingEllipsis";
import { disposeChart } from "../helpers/chartHelpers";
import { configureCategoryAxis, configureValueAxis, legendConfiguration } from "../helpers/configurations";
import { oceChartConfiguration } from "../helpers/oceConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: Array<Record<string, number | string>>;
  seriesList: Array<{ name: string; field: string }>;
  theme: ThemeInt;
  chartId: string;
  isLoading: boolean;
}

class MetricChannelShareChart extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  chart!: XYChart;
  chartId = this.props.chartId;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    //Handle refreshing the chart when the dataset changes
    if (!_.isEqual(prevProps, this.props)) {
      disposeChart(this.chartId).then((res) => {
        this.initChart();
      });
    }
  }

  componentWillUnmount() {
    disposeChart(this.chartId);
  }

  initChart() {
    const { data, seriesList, theme } = this.props;
    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.data = data;
    // this.chart.numberFormatter.numberFormat = "#.0a";
    // this.chart.colors.list = MEDIA_MIX_CHART_COLORS.map((colorStr: string) => color(colorStr));

    // Legend configuration
    this.chart.legend = legendConfiguration(theme);
    this.chart.legend.marginTop = 10;
    this.chart.legend.marginBottom = 30;
    this.chart.colors.list = CCS_ANALYSIS_COLORS.map((item) => color(item));

    // Create Axes
    const categoryAxis = this.chart.xAxes.push(new CategoryAxis());
    categoryAxis.dataFields.category = "category";
    configureCategoryAxis(categoryAxis, theme);
    categoryAxis.renderer.minGridDistance = 10;

    const valueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(valueAxis, theme);
    valueAxis.numberFormatter = new NumberFormatter();
    valueAxis.numberFormatter.numberFormat = "#.";
    valueAxis.renderer.labels.template.adapter.add("text", function (text) {
      return `${text}%`;
    });
    valueAxis.max = 100;

    seriesList?.forEach(({ name, field }, idx) => {
      const series = this.chart.series.push(new ColumnSeries());
      series.stacked = true;
      series.dataFields.categoryX = "category";
      series.name = name;
      series.dataFields.valueY = field;
      series.xAxis = categoryAxis;
      series.numberFormatter = new NumberFormatter();
      series.numberFormatter.numberFormat = "#.0a";
      series.columns.template.tooltipText = "{valueY}";
      if (series.tooltip) {
        series.tooltip.getFillFromObject = false;
        series.tooltip.autoTextColor = false;
        series.tooltip.background.fill = color(WHITE);
        series.tooltip.label.fill = color(BLACK);
      }

      series.columns.template.adapter.add("tooltipText", (value, target) => {
        const dataValue = target?.tooltipDataItem?.dataContext as { date: string; [index: string]: string | number };
        let text = "";

        this.chart.series.each((item) => {
          const value = item.dataFields.valueY ? dataValue[item.dataFields.valueY] : "";
          text += `[${item.stroke}]●[/] ${item.name}: ${value ? (value as number).toLocaleString() : ""}%\n`;
        });

        return text;
      });
    });
  }

  render() {
    return (
      <>
        <div className={"chartWrapper"}>
          <div id={this.props.chartId} data-test-id={this.props.chartId} className={"graph"} />
          <LoadingEllipsis isLoading={this.props.isLoading} />
        </div>
      </>
    );
  }
}

export default MetricChannelShareChart;
